/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { UapiMcpCore } from "../core.js";
import { encodeJSON } from "../lib/encodings.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { APIError } from "../models/errors/apierror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  PostTextAesDecryptAdvancedRequest,
  PostTextAesDecryptAdvancedRequest$zodSchema,
} from "../models/posttextaesdecryptadvancedop.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

/**
 * AES高级解密
 *
 * @remarks
 * 需要解密通过高级加密接口加密的数据？这个接口提供与加密接口完全配对的解密功能，支持相同的6种加密模式和3种填充方式。
 *
 * > [!IMPORTANT]
 * > **解密参数必须与加密时一致**
 * > 解密时，必须提供与加密时相同的密钥、模式和填充方式。对于非GCM模式，还需要提供加密时返回的IV。
 *
 * ## 功能概述
 * 这是一个功能完整的AES解密接口，能够解密通过高级加密接口加密的所有密文。支持所有6种加密模式和3种填充方式，与加密接口完全配对。
 *
 * ### 解密流程
 * 1. 获取加密时返回的密文和配置参数
 * 2. 使用相同的密钥、模式、填充方式和IV（如需要）
 * 3. 调用本接口进行解密
 * 4. 获取原始明文
 *
 * ### 支持的解密模式
 * - **GCM模式**（推荐）：自动验证数据完整性，如果密文被篡改会解密失败
 * - **CBC模式**：经典块解密模式，需要提供加密时的IV
 * - **CTR/OFB/CFB模式**：流密码解密，需要提供加密时的IV
 * - **ECB模式**：不需要IV，但安全性较低
 *
 * ### 填充方式处理
 * - **PKCS7填充**：解密后自动移除填充
 * - **Zero填充**：解密后自动移除0x00填充
 * - **None填充**：无填充处理
 *
 * ## 参数说明
 * - **`text`**: 待解密的密文（Base64编码，来自加密接口返回的ciphertext字段）
 * - **`key`**: 解密密钥（必须与加密时相同）
 * - **`mode`**: 加密模式（必须与加密时相同）
 * - **`padding`**: 填充方式（可选，默认PKCS7，必须与加密时相同）
 * - **`iv`**: 初始化向量（非GCM模式必须提供，Base64编码）
 *
 * ## 常见错误处理
 * 如果解密失败，请检查以下几点：
 * - 密钥是否与加密时完全相同
 * - 模式和填充方式是否匹配
 * - 非GCM模式下是否提供了正确的IV
 * - 密文是否完整且未被修改
 * - GCM模式下密文是否被篡改
 */
export function textPostTextAesDecryptAdvanced(
  client$: UapiMcpCore,
  request: PostTextAesDecryptAdvancedRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    Response,
    | APIError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
> {
  return new APIPromise($do(
    client$,
    request,
    options,
  ));
}

async function $do(
  client$: UapiMcpCore,
  request: PostTextAesDecryptAdvancedRequest,
  options?: RequestOptions,
): Promise<
  [
    Result<
      Response,
      | APIError
      | SDKValidationError
      | UnexpectedClientError
      | InvalidRequestError
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
    >,
    APICall,
  ]
> {
  const parsed$ = safeParse(
    request,
    (value$) => PostTextAesDecryptAdvancedRequest$zodSchema.parse(value$),
    "Input validation failed",
  );
  if (!parsed$.ok) {
    return [parsed$, { status: "invalid" }];
  }
  const payload$ = parsed$.value;
  const body$ = encodeJSON("body", payload$, { explode: true });
  const path$ = pathToFunc("/text/aes/decrypt-advanced")();

  const headers$ = new Headers(compactMap({
    "Content-Type": "application/json",
    Accept: "application/json",
  }));
  const securityInput = await extractSecurity(client$._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client$._options,
    baseURL: options?.serverURL ?? client$._baseURL ?? "",
    operationID: "post-text-aes-decrypt-advanced",
    oAuth2Scopes: null,
    resolvedSecurity: requestSecurity,
    securitySource: client$._options.security,
    retryConfig: options?.retries
      || client$._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || [
      "429",
      "500",
      "502",
      "503",
      "504",
    ],
  };

  const requestRes = client$._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path$,
    headers: headers$,
    body: body$,
    userAgent: client$._options.userAgent,
    timeoutMs: options?.timeoutMs || client$._options.timeoutMs
      || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req$ = requestRes.value;

  const doResult = await client$._do(req$, {
    context,
    errorCodes: [],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req$ }];
  }
  return [doResult, {
    status: "complete",
    "request": req$,
    response: doResult.value,
  }];
}
